<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Create OU</div>
        </template>

        <div class="flex flex-col gap-4">
            <div>
                <vx-label size="lg">Name</vx-label>
                <vx-input
                    data-test="org-unit-name"
                    type="text"
                    v-model="model.name"
                    size="lg"
                    :error="displayFirstError(validate.name.$errors)"
                />
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Create
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { orgUnitsClient } from '~/services';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { displayFirstError, groupAndOrgUnitName, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

const { $externalResults, catchError } = useFormRequestHandler();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = ref({
    name: '',
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const validationRules = computed(() => {
    const rules = {
        name: {
            required,
            maxLength: maxLength(80),
            format: groupAndOrgUnitName,
        },
    };

    return rules;
});

const validate = useVuelidate(validationRules, model, { $externalResults });

const emit = defineEmits(['created']);
const submitting = ref(false);

watch(visible, (newVisible) => {
    if (!newVisible) {
        model.value.name = '';
        validate.value.$reset();
    }
});

const submit = async () => {
    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const response = await orgUnitsClient.createOrgUnit(props.teamId, {
            name: model.value.name,
        });

        emit('created', response.data);

        window.location.href = `/settings/teams/${props.teamId}/org-units/${response.data.id}/groups`;
    } catch (e) {
        catchError(e);
        submitting.value = false;
    }
};
</script>
